import React, { FC, useContext, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import useIsMobileDimension from "../../hooks/useIsMobileDimension";
import EmpowerAlertImage from "@assets/images/empower_alert_image.png";
import { VideoPlayer } from "@app/components";
import {
  ConfigContext,
  IntlContext,
  Language,
  NotificationContext,
} from "@app/provider";
import Dialog from "@natera/material/lib/dialog";
import R from "ramda";

import Svg from "@natera/material/lib/svg";
import EmailSvg from "@assets/svg/icons/email2.svg";
import PhoneSvg from "@assets/svg/icons/phone.svg";

import "./empowerAlertDialog.scss";

const messages = defineMessages({
  empowerAlertPageTitle: {
    id: "empowerAlertPageTitle",
    defaultMessage: "Hereditary Cancer Testing",
  },
  empowerAlertPageHeader: {
    id: "empowerAlertPageHeader",
    defaultMessage: "Understand Your Risk. Take Control of Your Health.",
  },
  empowerAlertPageDescription: {
    id: "empowerAlertPageDescription",
    defaultMessage:
      "{hereditaryCancerTesting} is an important tool to understand your risk for certain cancers and take steps toward proactive care. If you’ve been diagnosed with cancer, knowing whether you have a hereditary cancer-related gene mutation can help guide your treatment, inform surgical decisions, prevent future cancers, and even provide insights for your family members.",
  },
  empowerAlertPageDescriptionLinkText: {
    id: "empowerAlertPageDescriptionLinkText",
    defaultMessage: "Hereditary cancer testing",
  },
  empowerAlertPageHereditaryCancerVideoText: {
    id: "empowerAlertPageHereditaryCancerVideoText",
    defaultMessage: "Getting Started with the Empower Hereditary Cancer Test",
  },
  empowerAlertPageEligibilityHeader: {
    id: "empowerAlertPageEligibilityHeader",
    defaultMessage: "Am I eligible?",
  },
  empowerAlertPageEligibilityInfo: {
    id: "empowerAlertPageEligibilityInfo",
    defaultMessage:
      "Based on your personal and family history provided to Natera, you meet national criteria for hereditary cancer testing. The general criteria for hereditary cancer testing include:",
  },
  empowerAlertPageEligibilityInfoItem1: {
    id: "empowerAlertPageEligibilityInfoItem1",
    defaultMessage: "Being diagnosed with breast cancer at age 65 or younger.",
  },
  empowerAlertPageEligibilityInfoItem2: {
    id: "empowerAlertPageEligibilityInfoItem2",
    defaultMessage:
      "Being diagnosed with colorectal, ovarian, endometrial, uterine, or pancreatic cancer at any age.",
  },
  empowerAlertPageNextStepsHeader: {
    id: "empowerAlertPageNextStepsHeader",
    defaultMessage: "What Are the Next Steps?",
  },
  empowerAlertPageNextStepsImageAltText: {
    id: "empowerAlertPageNextStepsImageAltText",
    defaultMessage: "Next steps image",
  },
  empowerAlertPageNextStepsItem1: {
    id: "empowerAlertPageNextStepsItem1",
    defaultMessage:
      "Talk to your healthcare provider about whether hereditary cancer testing is right for you.",
  },
  empowerAlertPageNextStepsItem2: {
    id: "empowerAlertPageNextStepsItem2",
    defaultMessage:
      "Ask your healthcare provider to request an Empower test with your next Signatera test.",
  },
  empowerAlertPageNextStepsItem3: {
    id: "empowerAlertPageNextStepsItem3",
    defaultMessage:
      "Once approved, you’ll receive a simple testing kit to collect your sample, which will be analyzed in our lab.",
  },
  empowerAlertPageCancerTestingHeader: {
    id: "empowerAlertPageCancerTestingHeader",
    defaultMessage: "Why Consider Hereditary Cancer Testing?",
  },
  empowerAlertPageCancerTestingItem1: {
    id: "empowerAlertPageCancerTestingItem1",
    defaultMessage:
      "Personalized Treatment Options: Certain treatments, like PARP inhibitors or specific chemotherapies, work better for individuals with hereditary mutations.",
  },
  empowerAlertPageCancerTestingItem2: {
    id: "empowerAlertPageCancerTestingItem2",
    defaultMessage:
      "Proactive Care for You and Your Family: Understanding your genetic risk can help guide screening and preventive strategies for you and your loved ones.",
  },
  empowerAlertPageCancerTestingItem3: {
    id: "empowerAlertPageCancerTestingItem3",
    defaultMessage:
      "Surgical Guidance: Genetic insights can inform decisions about surgery, such as risk-reducing procedures.",
  },
  empowerAlertPageCancerTestingItem4: {
    id: "empowerAlertPageCancerTestingItem4",
    defaultMessage:
      "Prevent Future Cancers: Certain hereditary cancer-related gene mutations can increase the risk for other cancers. Knowing early can empower you to take action for earlier screening and prevention.",
  },
  empowerAlertPageHowItWorksHeader: {
    id: "empowerAlertPageHowItWorksHeader",
    defaultMessage: "How Empower Works",
  },
  empowerAlertPageHowItWorksDescription: {
    id: "empowerAlertPageHowItWorksDescription",
    defaultMessage:
      "Natera’s Empower™ hereditary cancer testing is designed to provide clear answers you can act on, with:",
  },
  empowerAlertPageHowItWorksItems1: {
    id: "empowerAlertPageHowItWorksItems1",
    defaultMessage:
      "Convenient Testing: Provide a single blood or saliva sample, and Natera works with your clinic to deliver it to our lab for quick and thorough testing.",
  },
  empowerAlertPageHowItWorksItems2: {
    id: "empowerAlertPageHowItWorksItems2",
    defaultMessage:
      "Concise Reporting: Easy-to-understand results that align with the latest medical guidelines.",
  },
  empowerAlertPageHowItWorksItems3: {
    id: "empowerAlertPageHowItWorksItems3",
    defaultMessage:
      "Comprehensive Support: Access to Natera’s board-certified genetic counselors for pre- and post-test information.",
  },
  empowerAlertPageHowItWorksItems4: {
    id: "empowerAlertPageHowItWorksItems4",
    defaultMessage:
      "Affordability: In-network coverage with many insurance plans and robust billing support programs.",
  },
  empowerAlertPageTestimonialsHeader: {
    id: "empowerAlertPageTestimonialsHeader",
    defaultMessage: "Testimonials",
  },
  empowerAlertPageTestimonialsFirstVideoTitle: {
    id: "empowerAlertPageTestimonialsFirstVideoTitle",
    defaultMessage: "Alicia’s Story",
  },
  empowerAlertPageTestimonialsFirstVideoText: {
    id: "empowerAlertPageTestimonialsFirstVideoText",
    defaultMessage:
      "Hear from Alicia about how her Lynch Syndrome diagnosis put a name to her family’s cancer history and helped find her cancer early.",
  },
  empowerAlertPageHelpHeader: {
    id: "empowerAlertPageHelpHeader",
    defaultMessage: "We’re here to help!",
  },
  empowerAlertPageHelpDescription: {
    id: "empowerAlertPageHelpDescription",
    defaultMessage:
      "For more information, contact us to speak with our Patient Coordinator team",
  },
  empowerAlertPageHelpPhone: {
    id: "empowerAlertPageHelpPhone",
    defaultMessage: "650.446.4657",
  },
});

interface EmpowerAlertDialogProps {
  onCloseDialog: () => void;
}

const EmpowerAlertDialog: FC<EmpowerAlertDialogProps> = ({ onCloseDialog }) => {
  const intl = useIntl();
  const isMobile = useIsMobileDimension();
  const { currentLanguage } = React.useContext(IntlContext);
  const { config } = React.useContext(ConfigContext);
  const { clear } = useContext(NotificationContext);

  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [firstVideoUrl, setFirstVideoUrl] = useState<string>("");

  const HEREDITARY_CANCER_VIDEO_LINK =
    config.links.EMPOWER_HEREDITARY_CANCER_VIDEO_LINK[
      currentLanguage === Language.EN ? "EN" : "ES"
    ] || "";

  const TESTIMONIALS_VIDEO = {
    firstStory: "https://natera.wistia.com/medias/1qrg3qvucb",
  };

  React.useEffect(() => {
    const loadVideoUrls = async () => {
      const firstUrl = R.last(TESTIMONIALS_VIDEO.firstStory.split("/")) ?? "";
      if (firstUrl) {
        setFirstVideoUrl(firstUrl);
      }
    };
    loadVideoUrls();
  }, []);

  React.useEffect(() => {
    if (isMobile) {
      document
        .querySelector(".empower-alert-dialog.mobile")
        ?.classList.add("animate-up");
    }
  }, [isMobile]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      clear();
      onCloseDialog();
    }, 600);
  };

  return (
    <Dialog
      title={intl.formatMessage(messages.empowerAlertPageTitle)}
      showCrossButton
      onClose={handleClose}
      className={`empower-alert-dialog ${isMobile ? "mobile" : ""} ${
        isClosing ? "animate-down" : ""
      }`}
    >
      <div className="empower-alert-dialog__content">
        <section>
          <h2 className="empower-alert-dialog__content__header">
            {intl.formatMessage(messages.empowerAlertPageHeader)}
          </h2>
          <p className="empower-alert-dialog__content__description">
            {intl.formatMessage(messages.empowerAlertPageDescription, {
              hereditaryCancerTesting: (
                <a
                  className="empower-alert-dialog__content__resources-link"
                  href={config.links.EMPOWER_HEREDITARY_CANCER_TESTING}
                  target="_blank"
                  rel="noreferrer"
                >
                  {intl.formatMessage(
                    messages.empowerAlertPageDescriptionLinkText
                  )}
                </a>
              ),
            })}
          </p>
          <div className="empower-alert-dialog__content__hereditary-cancer-video">
            <VideoPlayer
              id={HEREDITARY_CANCER_VIDEO_LINK}
              thumbnailAltText={intl.formatMessage(
                messages.empowerAlertPageHereditaryCancerVideoText
              )}
            />
          </div>
        </section>
        <section>
          <h3 className="empower-alert-dialog__content__eligibility">
            {intl.formatMessage(messages.empowerAlertPageEligibilityHeader)}
          </h3>
          <p className="empower-alert-dialog__content__eligibility-info">
            {intl.formatMessage(messages.empowerAlertPageEligibilityInfo)}
          </p>
          <ul>
            <li>
              {intl.formatMessage(
                messages.empowerAlertPageEligibilityInfoItem1
              )}
            </li>
            <li>
              {intl.formatMessage(
                messages.empowerAlertPageEligibilityInfoItem2
              )}
            </li>
          </ul>
        </section>
        <section>
          <h3 className="empower-alert-dialog__content__next-steps">
            {intl.formatMessage(messages.empowerAlertPageNextStepsHeader)}
          </h3>
          <img
            src={EmpowerAlertImage}
            alt={intl.formatMessage(
              messages.empowerAlertPageNextStepsImageAltText
            )}
            className="empower-alert-dialog__content__next-steps-image"
          />
          <ol className="empower-alert-dialog__content__next-steps-info">
            <li>
              {intl.formatMessage(messages.empowerAlertPageNextStepsItem1)}
            </li>
            <li>
              {intl.formatMessage(messages.empowerAlertPageNextStepsItem2)}
            </li>
            <li>
              {intl.formatMessage(messages.empowerAlertPageNextStepsItem3)}
            </li>
          </ol>
        </section>
        <section className="empower-alert-dialog__content__cancer__testing">
          <h3 className="empower-alert-dialog__content__cancer__testing__title">
            {intl.formatMessage(messages.empowerAlertPageCancerTestingHeader)}
          </h3>
          <ul className="empower-alert-dialog__content__cancer__testing__list">
            <li>
              {intl.formatMessage(messages.empowerAlertPageCancerTestingItem1)}
            </li>
            <li>
              {intl.formatMessage(messages.empowerAlertPageCancerTestingItem2)}
            </li>
            <li>
              {intl.formatMessage(messages.empowerAlertPageCancerTestingItem3)}
            </li>
            <li>
              {intl.formatMessage(messages.empowerAlertPageCancerTestingItem4)}
            </li>
          </ul>
        </section>
        <section className="empower-alert-dialog__content__how__it__works">
          <h3 className="empower-alert-dialog__content__how__it__works__title">
            {intl.formatMessage(messages.empowerAlertPageHowItWorksHeader)}
          </h3>
          <p>
            {intl.formatMessage(messages.empowerAlertPageHowItWorksDescription)}
          </p>
          <ul className="empower-alert-dialog__content__how__it__works__list">
            <li>
              {intl.formatMessage(messages.empowerAlertPageHowItWorksItems1)}
            </li>
            <li>
              {intl.formatMessage(messages.empowerAlertPageHowItWorksItems2)}
            </li>
            <li>
              {intl.formatMessage(messages.empowerAlertPageHowItWorksItems3)}
            </li>
            <li>
              {intl.formatMessage(messages.empowerAlertPageHowItWorksItems4)}
            </li>
          </ul>
        </section>
        <section className="empower-alert-dialog__content__testimonials">
          <h3 className="empower-alert-dialog__content__testimonials-title">
            {intl.formatMessage(messages.empowerAlertPageTestimonialsHeader)}
          </h3>
          <div>
            {firstVideoUrl && (
              <div className="empower-alert-dialog__content__testimonials-video">
                <div className="empower-alert-dialog__content__testimonials-video__description">
                  <h3 className="empower-alert-dialog__content__testimonials-video__title">
                    {intl.formatMessage(
                      messages.empowerAlertPageTestimonialsFirstVideoTitle
                    )}
                  </h3>
                  <p className="empower-alert-dialog__content__testimonials-video__text">
                    {intl.formatMessage(
                      messages.empowerAlertPageTestimonialsFirstVideoText
                    )}
                  </p>
                </div>
                <VideoPlayer
                  id={firstVideoUrl}
                  thumbnailAltText={intl.formatMessage(
                    messages.empowerAlertPageTestimonialsFirstVideoText
                  )}
                />
              </div>
            )}
          </div>
        </section>
        <section className="empower-alert-dialog__help">
          <h3 className="empower-alert-dialog__help__title">
            {intl.formatMessage(messages.empowerAlertPageHelpHeader)}
          </h3>
          <p>{intl.formatMessage(messages.empowerAlertPageHelpDescription)}</p>
          <div>
            <p>
              <Svg content={PhoneSvg} />
              <a
                className="empower-alert-dialog__help-link"
                href={`tel:${intl.formatMessage(
                  messages.empowerAlertPageHelpPhone
                )}`}
              >
                {intl.formatMessage(messages.empowerAlertPageHelpPhone)}
              </a>
            </p>
            <p>
              <Svg content={EmailSvg} />
              <a
                className="empower-alert-dialog__help-link"
                href={`mailto:${config.emails.PATIENT_COORDINATOR}`}
              >
                {config.emails.PATIENT_COORDINATOR}
              </a>
            </p>
          </div>
        </section>
      </div>
    </Dialog>
  );
};

export default EmpowerAlertDialog;
